import { useState } from 'react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { usePrevalidate } from '@/hooks/usePatching'
import { toPatchApiError } from '@/services/patching'
import type { PatchEnvironment, PrePatchCheck } from '@/types/patching'
import styles from './Patching.module.css'

interface PrePatchPanelProps {
  campaignId: string
  env: PatchEnvironment
}

/** Runs the backend pre-patch checks. A failed mandatory check blocks scheduling. */
export function PrePatchPanel({ campaignId, env }: PrePatchPanelProps) {
  const run = usePrevalidate()
  const [rows, setRows] = useState<PrePatchCheck[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onRun() {
    setError(null)
    try {
      setRows(await run.mutateAsync({ campaignId, env }))
    } catch (err) {
      setError(toPatchApiError(err).message)
    }
  }

  return (
    <div className={styles.stack} data-testid="prepatch-panel">
      <p className={styles.muted}>
        Checks resource access, power state, assessment freshness and eligibility. A failed mandatory check blocks scheduling.
        This does not install patches.
      </p>
      <div className={styles.actionRow}>
        <Button variant="secondary" onClick={onRun} loading={run.isPending} disabled={run.isPending}>
          Run pre-patch validation
        </Button>
      </div>
      {error && (
        <div className={clsx(styles.alert, styles.alertError)} role="alert">
          {error}
        </div>
      )}
      {rows && rows.length === 0 && <p className={styles.muted}>No checks were returned.</p>}
      {rows && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Resource</th>
                <th>Check</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.resource_name}-${row.check}-${row.detail}`}>
                  <td>{row.resource_name}</td>
                  <td>{row.check}{row.mandatory ? '' : ' (optional)'}</td>
                  <td>{row.status}</td>
                  <td>{row.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
